/**
 * API Configuration Service
 * Handles routing between public and private API endpoints
 */

export interface ApiEndpoints {
  public: string;
  private: string;
}

export interface ApiRouteConfig {
  pattern: string | RegExp;
  endpoint: 'public' | 'private';
  description: string;
}

// Define routing patterns for different API endpoints
export const API_ROUTES: ApiRouteConfig[] = [
  // Auth0 authentication endpoints - route to public endpoint
  {
    pattern: /^\/accounts-v2\/auth0\/users\/auth/,
    endpoint: 'public',
    description: 'Auth0 user authentication endpoints',
  },
  {
    pattern: /^\/auth\/callback/,
    endpoint: 'public',
    description: 'Auth0 callback endpoints',
  },
  {
    pattern: /^\/auth\/login/,
    endpoint: 'public',
    description: 'Auth0 login endpoints',
  },
  {
    pattern: /^\/auth\/logout/,
    endpoint: 'public',
    description: 'Auth0 logout endpoints',
  },
  {
    pattern: /^\/auth\/refresh/,
    endpoint: 'public',
    description: 'Auth0 token refresh endpoints',
  },
  // Demo endpoints - route to public endpoint
  {
    pattern: /^\/accounts-v2\/auth0\/users\/auth\/demo/,
    endpoint: 'public',
    description: 'Demo authentication endpoints',
  },
  // External services - route to public endpoint
  {
    pattern: /^https:\/\/hubspot-eucwb5cebqd9fjby\.z01\.azurefd\.net\/hubspot\//,
    endpoint: 'public',
    description: 'HubSpot integration endpoints',
  },
  // All other endpoints - route to private endpoint (default)
  {
    pattern: /.*/,
    endpoint: 'private',
    description: 'All other API endpoints',
  },
];

/**
 * Get the appropriate base URL for a given API path
 * @param path - The API path to route
 * @returns The base URL (public or private) for the given path
 */
export const getApiBaseUrl = (path: string): string => {
  const endpoints: ApiEndpoints = {
    public: import.meta.env.REACT_APP_PUBLIC_API_URL || import.meta.env.REACT_APP_CONTROLROOMS_API,
    private:
      import.meta.env.REACT_APP_PRIVATE_API_URL || import.meta.env.REACT_APP_CONTROLROOMS_API,
  };

  // Find the first matching route pattern
  const matchingRoute = API_ROUTES.find((route) => {
    if (typeof route.pattern === 'string') {
      return path.startsWith(route.pattern);
    }
    return route.pattern.test(path);
  });

  if (!matchingRoute) {
    console.warn(`No route pattern found for path: ${path}, defaulting to private endpoint`);
    return endpoints.private;
  }

  const baseUrl = endpoints[matchingRoute.endpoint];

  // Log routing decision in development
  if (import.meta.env.DEV) {
    console.log(`API Route: ${path} -> ${matchingRoute.endpoint} (${matchingRoute.description})`);
  }

  return baseUrl;
};

/**
 * Check if a path should bypass authentication
 * @param path - The API path to check
 * @returns True if the path should bypass authentication
 */
export const shouldBypassAuth = (path: string): boolean => {
  // Collect all patterns for endpoints routed to 'public'
  const bypassPatterns = API_ROUTES.filter((route) => route.endpoint === 'public').map(
    (route) => route.pattern,
  );

  return bypassPatterns.some((pattern) => {
    if (typeof pattern === 'string') {
      return path.startsWith(pattern);
    }
    return pattern.test(path);
  });
};

/**
 * Check if a path requires authentication
 * @param path - The API path to check
 * @returns True if the path requires authentication
 */
export const requiresAuth = (path: string): boolean => {
  return !shouldBypassAuth(path);
};
